import { useEffect, useRef, useState } from 'react';
import { UserPlus, MessageCircle, CalendarCheck, UserCheck } from 'lucide-react';

const steps = [
  {
    icon: UserPlus,
    step: '01',
    title: 'Applicant Applies',
    description: 'Someone applies on Indeed or you add them manually. Their info flows straight into iHireFast.',
    color: 'from-blue-500 to-blue-600',
  },
  {
    icon: MessageCircle,
    step: '02',
    title: 'Auto-Email Sent',
    description: 'Within seconds, they receive a personalized email with your Calendly link. No manual work required.',
    color: 'from-green-500 to-green-600',
  },
  {
    icon: CalendarCheck,
    step: '03',
    title: 'They Schedule',
    description: 'The applicant picks a time that works for them. Your calendar fills up while you sleep.',
    color: 'from-purple-500 to-purple-600',
  },
  {
    icon: UserCheck,
    step: '04',
    title: 'You Hire',
    description: 'Show up to the interview, review their resume in-app, and make the hire. Simple as that.',
    color: 'from-orange-500 to-orange-600',
  },
];

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const scrollProgress = Math.max(0, Math.min(1, 
        (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
      ));
      
      const newStep = Math.min(
        steps.length - 1,
        Math.floor(scrollProgress * steps.length)
      );
      setActiveStep(newStep);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="section-padding gradient-bg overflow-hidden">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            From application to{' '}
            <span className="gradient-text">hire in 4 steps</span>
          </h2>
          <p className="text-lg text-gray-600">
            No complex setup. No learning curve. Just connect your accounts and 
            watch the magic happen.
          </p>
        </div>

        {/* Steps */}
        <div className="relative max-w-5xl mx-auto">
          {/* Connection Line */}
          <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary-200 via-primary-400 to-primary-200 hidden md:block" />
          
          <div className="space-y-12 lg:space-y-24">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`relative flex flex-col lg:flex-row items-start gap-8 transition-all duration-500 ${
                  index <= activeStep ? 'opacity-100' : 'opacity-50'
                }`}
              >
                {/* Left Side (even steps) */}
                {index % 2 === 0 && (
                  <div className="hidden lg:flex lg:w-1/2 lg:justify-end lg:pr-12">
                    <div className="text-right">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                )}

                {/* Center Icon */}
                <div className="relative flex-shrink-0">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg ${
                    index <= activeStep ? 'scale-100' : 'scale-90'
                  } transition-transform duration-300`}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <span className="text-xs font-bold text-gray-400">{step.step}</span>
                  </div>
                </div>

                {/* Right Side (odd steps) */}
                {index % 2 === 1 && (
                  <div className="hidden lg:flex lg:w-1/2 lg:pl-12">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                )}

                {/* Mobile Content */}
                <div className="lg:hidden flex-1 pl-0 md:pl-0">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16 lg:mt-24">
          <a
            href="/app"
            className="btn-primary text-base px-8 py-4 inline-flex items-center gap-2"
          >
            Get Started Free
          </a>
          <p className="text-sm text-gray-500 mt-4">No credit card required. 14-day free trial.</p>
        </div>
      </div>
    </section>
  );
}
